import React from 'react';
import { DocumentTextIcon, CurrencyDollarIcon, CalendarIcon, BuildingStorefrontIcon, UserIcon } from '@heroicons/react/24/outline';

export const FacturaResumen = ({ data, abonoTotal }) => {
    if (!data) return <div className="p-8 text-center text-slate-500">No hay datos de factura disponibles.</div>;

    const montoProyectado = parseFloat(data.Valor_Cobrar_Proyectado || data.Proyectado || 0);
    
    const valTotal = parseFloat(data.Valor_Cobrado_Total || 0);
    const valCobrado = parseFloat(data.Valor_Cobrado || 0);
    const valCobrado2 = parseFloat(data.Cobrado || 0);
    const valAbono = parseFloat(abonoTotal || 0);
    
    // Tomar el primer valor mayor a 0, de lo contrario 0
    const montoCobrado = valTotal > 0 ? valTotal : (valAbono > 0 ? valAbono : (valCobrado > 0 ? valCobrado : valCobrado2));
    
    let porcentaje = 0;
    if (montoProyectado > 0) {
        porcentaje = Math.min(Math.max((montoCobrado / montoProyectado) * 100, 0), 100);
    }
    
    const avanceText = `${porcentaje.toFixed(0)}%`;
    const avanceWidth = `${porcentaje.toFixed(0)}%`;

    return (
        <div className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden shadow-lg p-6 w-full max-w-4xl mx-auto mt-4">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200">
                <div className="p-3 bg-indigo-100 text-indigo-600 rounded-xl">
                    <DocumentTextIcon className="w-6 h-6" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-slate-800">Detalle de Factura</h2>
                    <p className="text-slate-500 text-sm">Documento #{data.Numero_Documento || data.Factura_No || data.Documento || 'No disponible'}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h3 className="font-bold text-slate-700 uppercase text-xs tracking-wider border-b pb-2">Información Comercial</h3>
                    
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2 text-slate-600">
                            <BuildingStorefrontIcon className="w-5 h-5" />
                            <span className="font-semibold text-sm">Almacén</span>
                        </div>
                        <span className="font-bold text-slate-800 text-right">{data.Almacen || data.Bodega || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2 text-slate-600">
                            <UserIcon className="w-5 h-5" />
                            <span className="font-semibold text-sm">Cobrador/Gestor</span>
                        </div>
                        <span className="font-bold text-slate-800 text-right">{data.Gestor || data.Cobrador || data.Vendedor || '-'}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2 text-slate-600">
                            <CalendarIcon className="w-5 h-5" />
                            <span className="font-semibold text-sm">Fecha Factura</span>
                        </div>
                        <span className="font-bold text-slate-800 text-right">
                            {data.Fecha_Factura ? new Date(data.Fecha_Factura).toLocaleDateString() : (data.Fecha_Emision || data.Fecha || '-')}
                        </span>
                    </div>
                </div>

                <div className="space-y-4">
                    <h3 className="font-bold text-slate-700 uppercase text-xs tracking-wider border-b pb-2">Resumen Financiero</h3>
                    
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2 text-slate-600">
                            <CurrencyDollarIcon className="w-5 h-5" />
                            <span className="font-semibold text-sm">Monto Proyectado</span>
                        </div>
                        <span className="font-bold text-slate-800 text-right">${montoProyectado.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100">
                        <div className="flex items-center gap-2 text-green-700">
                            <CurrencyDollarIcon className="w-5 h-5" />
                            <span className="font-semibold text-sm">Cobrado</span>
                        </div>
                        <span className="font-bold text-green-700 text-right">${montoCobrado.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-rose-50 rounded-xl border border-rose-100">
                        <div className="flex items-center gap-2 text-rose-700">
                            <CurrencyDollarIcon className="w-5 h-5" />
                            <span className="font-semibold text-sm">Saldo Vencido / Mora</span>
                        </div>
                        <span className="font-bold text-rose-700 text-right">
                            {data.Saldo_Vencido ?? data.SaldoVencido ? `$${parseFloat(data.Saldo_Vencido || data.SaldoVencido).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : (data.Dias_Mora_Proyectado ? `${data.Dias_Mora_Proyectado} días` : '0')}
                        </span>
                    </div>
                </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-200 bg-slate-50 p-4 rounded-xl flex items-center justify-between">
                <div>
                    <p className="text-xs font-bold text-slate-500 uppercase">Progreso de Pago</p>
                    <p className="text-sm font-semibold text-slate-700">{avanceText}</p>
                </div>
                <div className="w-2/3 bg-slate-200 rounded-full h-2.5">
                    <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500" style={{ width: avanceWidth }}></div>
                </div>
            </div>
        </div>
    );
};
